import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Loader2,
  Plus,
  Trash2,
  Upload,
  AlertTriangle,
  Info,
  FolderTree,
  Tag,
  BookOpen,
  Layers,
  CheckCircle2,
  XCircle,
  Pencil,
  Sparkles,
  ScrollText,
  HelpCircle,
  History,
  Globe,
  GitBranch,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { agruparResultados } from './../utils/agrupar-resultados';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { MasterDataImportDialog } from '@/features/master-data/components/import-csv-dialog';
import {
  tariffCatalogService,
  aPorcentaje,
  ORIGEN_LABELS,
  type ResultadoBusqueda,
  type RespuestaBusqueda,
  type RespuestaArbol,
  type TenantSubheading,
  type TariffSynonym,
  type TariffVersion,
  type ResultadoImport,
  type ConteoTerminoBusqueda,
} from '../services/tariff-catalog-service';
import {
  terminosConConteo,
  hayTerminosSinRespaldo,
  traduccionesVisibles,
  descartadoVisible,
} from '../utils/panel-interpretacion';
import { ResultadosArbol } from './resultados-arbol';
import { ArbolArancel } from './arbol-arancel';
import { CalificarBusqueda } from './calificar-busqueda';

export type SubVista = 'explorar' | 'aperturas' | 'sinonimos' | 'versiones' | 'historial';

export const SUB_VISTAS: Array<{
  id: SubVista;
  label: string;
  icon: React.ElementType;
  /** Solo para administradores. El historial muestra el gasto y lo que busca
   *  cada usuario: es un panel de gestión, no de trabajo diario. */
  soloAdmin?: boolean;
}> = [
  { id: 'explorar', label: 'Explorar el arancel', icon: BookOpen },
  { id: 'aperturas', label: 'Mis aperturas', icon: Layers },
  { id: 'sinonimos', label: 'Mis sinónimos', icon: Tag },
  { id: 'versiones', label: 'Versiones del arancel', icon: FolderTree },
  { id: 'historial', label: 'Historial y gasto', icon: History, soloAdmin: true },
];

/**
 * Los términos con los que se buscó, cada uno con cuántas partidas encontró.
 *
 * Antes se pintaban todos iguales, en verde: el usuario veía "para trabajos de
 * industria" con el mismo peso que "calzado" sin manera de saber que el arancel
 * no usa esa frase y que se descartó del puntaje.
 */
function TerminosBuscados({
  terminosLegales,
  conteos,
}: {
  terminosLegales: string[];
  conteos?: ConteoTerminoBusqueda[];
}) {
  const terminos = terminosConConteo(terminosLegales, conteos);

  return (
    <>
      <span className="flex flex-wrap items-center gap-1">
        {terminos.map((t) =>
          t.sinRespaldo ? (
            <Badge
              key={t.termino}
              variant="outline"
              className="font-normal border-dashed text-muted-foreground line-through"
            >
              {t.termino}
            </Badge>
          ) : (
            <Badge key={t.termino} variant="secondary" className="font-normal">
              {t.termino}
              {t.partidas !== null && (
                <span className="ml-1.5 tabular-nums text-muted-foreground">· {t.partidas}</span>
              )}
            </Badge>
          )
        )}
      </span>
      {hayTerminosSinRespaldo(terminos) && (
        <span className="basis-full text-xs text-muted-foreground">
          Lo tachado no existe en el arancel, así que no se usó para ordenar los resultados.
        </span>
      )}
    </>
  );
}

/**
 * Qué palabra del usuario se cambió por cuál del arancel. Sin esta línea,
 * buscar "que cubran el tobillo" cuando el usuario escribió "botas" se ve como
 * si el buscador hubiera ignorado lo que pidió.
 */
function LineaTraduje({ traducciones }: { traducciones?: Array<{ de: string; a: string }> }) {
  const visibles = traduccionesVisibles(traducciones);
  if (visibles.length === 0) return null;

  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="w-16 shrink-0 text-xs text-muted-foreground">Traduje</span>
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {visibles.map((t) => (
          <span key={`${t.de}->${t.a}`} className="inline-flex items-center gap-1.5">
            <span className="text-muted-foreground">{t.de}</span>
            <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="font-medium">{t.a}</span>
          </span>
        ))}
      </span>
    </div>
  );
}

/**
 * Lo que la IA dejó de lado por no cambiar la partida (marca, modelo, medidas).
 *
 * Lo traducido NO entra: el intérprete devuelve las mismas palabras en las dos
 * listas, y el panel llegó a decir a la vez "traduje botas" y "ignoré botas".
 */
function LineaIgnore({
  descartado,
  traducciones,
}: {
  descartado?: string[];
  traducciones?: Array<{ de: string; a: string }>;
}) {
  const ignorados = descartadoVisible(descartado, traducciones);
  if (ignorados.length === 0) return null;

  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="w-16 shrink-0 text-xs text-muted-foreground">Ignoré</span>
      <span className="flex flex-wrap items-baseline gap-1">
        {ignorados.map((t) => (
          <Badge key={t} variant="outline" className="font-normal text-muted-foreground">
            {t}
          </Badge>
        ))}
        <span className="text-xs text-muted-foreground">— no hacían falta para clasificar</span>
      </span>
    </div>
  );
}

/** Muestra 0.05 como "5%". Las alícuotas se guardan como fracción. */
export function pct(v: string | number): string {
  return `${aPorcentaje(v).toFixed(2).replace(/\.00$/, '')}%`;
}

/**
 * Renderiza la cita de una nota legal resaltando lo que matcheó. El backend
 * manda <b></b> (ts_headline); acá se parte el texto y se pinta con nodos
 * React — nunca dangerouslySetInnerHTML: el texto de una nota es dato, no HTML.
 */
function CitaResaltada({ fragmento }: { fragmento: string }) {
  const partes = fragmento.split(/<\/?b>/g);
  return (
    <>
      {partes.map((p, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="rounded-sm bg-alerta-suave px-0.5 font-semibold text-inherit ">
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

/**
 * Aproximación en el cliente del router del backend: ¿esta consulta va a
 * pasar por la IA intérprete? Solo decide el TEXTO de la espera ("estamos
 * traduciendo tu descripción...") — el camino real lo dice la respuesta.
 */
function pareceDescripcionProducto(q: string): boolean {
  const limpio = q.trim();
  if (!limpio || /^[\d.\s]+$/.test(limpio)) return false;
  return limpio.split(/\s+/).length >= 3;
}

export const NOTA_SCOPE_LABELS: Record<string, string> = {
  SECCION: 'Nota de la sección',
  CAPITULO: 'Nota del capítulo',
};

// ============================================================
// Buscador reutilizable de partida oficial (para aperturas y sinónimos)
// ============================================================
function BuscadorPartida({
  onSeleccionar,
  seleccionada,
}: {
  onSeleccionar: (p: ResultadoBusqueda | null) => void;
  seleccionada: ResultadoBusqueda | null;
}) {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState<ResultadoBusqueda[]>([]);
  const [buscando, setBuscando] = useState(false);

  const buscar = async () => {
    if (q.trim().length < 2) return;
    setBuscando(true);
    try {
      const r = await tariffCatalogService.buscar({ q: q.trim(), limit: 15 });
      setResultados((r.resultados ?? []).filter((x) => x.origen === 'OFICIAL'));
    } finally {
      setBuscando(false);
    }
  };

  if (seleccionada) {
    return (
      <div className="flex items-center justify-between rounded-md border p-3 bg-muted/40">
        <div className="min-w-0">
          <p className="font-mono text-sm font-semibold">{seleccionada.code}</p>
          <p className="text-sm text-muted-foreground truncate">{seleccionada.description}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onSeleccionar(null)}>
          Cambiar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          placeholder="Buscá por descripción o por código (ej: 8471)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), buscar())}
        />
        <Button type="button" variant="outline" onClick={buscar} disabled={buscando}>
          {buscando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      {resultados.length > 0 && (
        <div className="max-h-52 overflow-y-auto rounded-md border divide-y">
          {resultados.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => onSeleccionar(r)}
              className="w-full text-left p-2 hover:bg-muted/60 transition-colors"
            >
              <span className="font-mono text-xs font-semibold">{r.code}</span>
              <span className="text-sm ml-2">{r.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// Vista 1 — Explorar
// ============================================================
export function VistaExplorar({ onAbrirFicha }: { onAbrirFicha: (code: string) => void }) {
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [capitulo, setCapitulo] = useState<string | undefined>();
  // El capítulo abierto DESDE los resultados («Ver capítulo completo», C2 del
  // plan 178). Distinto de `capitulo`: no filtra la búsqueda ni la pisa — es
  // una vista del libro por encima de los resultados, con vuelta atrás.
  const [capituloVista, setCapituloVista] = useState<string | null>(null);
  const [soloIce, setSoloIce] = useState(false);
  const [respuesta, setRespuesta] = useState<RespuestaBusqueda | null>(null);
  const [arbol, setArbol] = useState<RespuestaArbol | null>(null);
  const [cargando, setCargando] = useState(false);
  // La banda "cómo entendimos tu búsqueda" es clave para confiar en la IA,
  // pero quien ya le tomó la mano puede preferirla plegada — la preferencia
  // se recuerda entre sesiones.
  const [bandaAbierta, setBandaAbierta] = useState<boolean>(
    () => localStorage.getItem('prixia.arancel.banda-ia') !== 'plegada'
  );
  const alternarBanda = () =>
    setBandaAbierta((v) => {
      localStorage.setItem('prixia.arancel.banda-ia', v ? 'plegada' : 'abierta');
      return !v;
    });

  // `consulta` permite disparar una búsqueda con un texto que todavía no
  // está en el estado (los ejemplos clicables) — el estado de React es
  // asíncrono y `buscar()` leería el valor viejo.
  const buscar = useCallback(
    async (consulta?: string) => {
      const texto = (consulta ?? q).trim();
      setCargando(true);
      try {
        const r = await tariffCatalogService.buscar({
          q: texto || undefined,
          chapter: capitulo,
          hasIce: soloIce,
          limit: 100,
        });
        setRespuesta(r);
        // Una búsqueda nueva reemplaza lo que se estaba mirando del libro.
        setCapituloVista(null);
      } catch (e: unknown) {
        toast({
          title: 'Error al buscar',
          description: e instanceof Error ? e.message : undefined,
          variant: 'destructive',
        });
      } finally {
        setCargando(false);
      }
    },
    [q, capitulo, soloIce, toast]
  );

  const buscarEjemplo = (texto: string) => {
    setQ(texto);
    buscar(texto);
  };

  useEffect(() => {
    tariffCatalogService
      .arbol()
      .then(setArbol)
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capitulo, soloIce]);

  // La ficha vive en su propia vista (5 pestañas) — acá solo se navega.
  const abrirDetalle = (code: string) => {
    // En qué partida terminó (tarea D2): se captura solo, sin preguntarle nada.
    // Fire-and-forget y con el error tragado a propósito: es telemetría, y no
    // puede impedirle a nadie abrir una ficha.
    const id = respuesta?.busquedaId;
    if (id) void tariffCatalogService.registrarPartidaElegida(id, code).catch(() => undefined);
    onAbrirFicha(code);
  };

  const resultados = respuesta?.resultados ?? [];
  // Clarificación: los primeros N resultados son los candidatos empatados
  // (el backend ya los ordenó); el resto va a la tabla de abajo por si
  // ninguno encaja.
  const nClarif = respuesta?.requiereClarificacion
    ? (respuesta.candidatosClarificacion ?? Math.min(5, resultados.length))
    : 0;
  const resultadosTabla = nClarif > 0 ? resultados.slice(nClarif) : resultados;
  // Agrupar por rama solo cuando la consulta fue una PREGUNTA que pasó por la
  // IA. Navegar un capítulo entero o pedir un código son listas: ahí el árbol
  // sería un solo grupo gigante repitiendo su propio título.
  const enArbol =
    (respuesta?.camino === 'IA' || respuesta?.camino === 'OR_FALLBACK') &&
    !respuesta?.matchPorCodigo;

  // Elegir un capítulo SIN escribir nada es navegar el libro, no preguntar algo.
  // Ahí la lista plana no sirve: el arancel está lleno de "Los demás" que solo
  // significan algo colgados de su rama (tarea A3, vault 126 §3.1). Con texto
  // escrito manda la búsqueda.
  //
  // Se guarda el capítulo y no un booleano para que TypeScript sepa que abajo
  // no es `undefined`.
  const capituloNavegado = capitulo && q.trim() === '' ? capitulo : null;

  // Los capítulos que aparecieron en la búsqueda activa, para el panel
  // «En tu búsqueda» (frente B del plan 178): primero los que SON la cosa,
  // después los que solo la mencionan. Es el mismo agrupado de la vista de
  // resultados, así el panel y los grupos cuentan lo mismo.
  const capitulosDeBusqueda = (() => {
    if (q.trim() === '' || resultados.length === 0) return [];
    const { principales, otrasMenciones } = agruparResultados(resultados);
    return [
      ...principales.map((c) => ({ ...c, menciona: false })),
      ...otrasMenciones.map((c) => ({ ...c, menciona: true })),
    ];
  })();

  return (
    <div className="space-y-4">
      {/* Buscador */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscá una mercadería (ej: algodón, generador) o un código (ej: 8471)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && buscar()}
          />
        </div>
        <Button onClick={() => buscar()} disabled={cargando}>
          {cargando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Buscar
        </Button>
        <label className="flex items-center gap-2 text-sm whitespace-nowrap">
          <input type="checkbox" checked={soloIce} onChange={(e) => setSoloIce(e.target.checked)} />
          Solo con ICE
        </label>
      </div>

      {/* Ejemplos clicables: que quien entra por primera vez vea al instante
          QUÉ se puede escribir acá — descripción con marca, palabra suelta o
          código, y que un clic se lo demuestre. */}
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <span>Podés escribir como quieras — probá:</span>
        {['Toyota Hilux diesel 2024', 'ropa', '8471.30'].map((ej) => (
          <button
            key={ej}
            type="button"
            onClick={() => buscarEjemplo(ej)}
            className="rounded-full border px-2.5 py-0.5 transition-colors hover:border-primary hover:text-foreground"
          >
            {ej}
          </button>
        ))}
      </div>

      {capitulo && (
        <div className="flex items-center gap-2 text-sm">
          <Badge variant="secondary">Capítulo {capitulo}</Badge>
          <Button variant="ghost" size="sm" onClick={() => setCapitulo(undefined)}>
            Quitar filtro
          </Button>
        </div>
      )}

      {respuesta?.matchPorCodigo && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Info className="h-3 w-3" /> Buscaste algo que parece un código, así que se priorizó el
          match por código sobre el textual.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        {/* Árbol de navegación */}
        <div className="space-y-2">
          <p className="text-sm font-semibold flex items-center gap-2">
            <FolderTree className="h-4 w-4" /> Navegar por capítulo
          </p>
          <p className="text-xs text-muted-foreground">
            Un tercio del arancel no tiene descripción utilizable. Si no lo encontrás buscando,
            llegá por acá.
          </p>
          {/* Lo que la búsqueda activa trajo, capítulo por capítulo. Clickear
              re-busca DENTRO del capítulo (el filtro ahora entra al motor
              antes del tope — frente B del plan 178). */}
          {capitulosDeBusqueda.length > 0 && !capituloNavegado && (
            <div className="rounded-md border">
              <p className="border-b bg-muted/40 px-2 py-1.5 text-xs font-semibold">
                En tu búsqueda
              </p>
              {capitulosDeBusqueda.map((c) => (
                <button
                  key={c.capitulo}
                  onClick={() => setCapitulo(c.capitulo)}
                  className={`flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-sm hover:bg-muted/60 ${
                    capitulo === c.capitulo ? 'bg-muted font-medium' : ''
                  }`}
                >
                  <span className="min-w-0 truncate">
                    <span className="mr-1 font-mono text-xs">{c.capitulo}</span>
                    {c.nombre}
                    {c.menciona && (
                      <span className="ml-1 text-xs text-muted-foreground">solo menciona</span>
                    )}
                  </span>
                  <Badge variant="secondary" className="shrink-0">
                    {c.total}
                  </Badge>
                </button>
              ))}
            </div>
          )}
          <div className="max-h-[520px] overflow-y-auto rounded-md border divide-y">
            {(arbol?.secciones ?? []).map((s) => (
              <div key={s.section} className="p-2">
                <p className="text-xs font-semibold text-muted-foreground mb-1">
                  Sección {s.section}
                </p>
                {s.capitulos.map((c) => (
                  <button
                    key={c.chapter}
                    onClick={() => setCapitulo(c.chapter)}
                    className={`w-full text-left text-sm px-2 py-1 rounded hover:bg-muted/60 flex items-center justify-between gap-2 ${
                      capitulo === c.chapter ? 'bg-muted font-medium' : ''
                    }`}
                  >
                    <span className="truncate">
                      <span className="font-mono text-xs mr-1">{c.chapter}</span>
                      {c.chapterName}
                    </span>
                    <Badge
                      variant={c.partidas < 5 ? 'destructive' : 'secondary'}
                      className="shrink-0"
                    >
                      {c.partidas}
                    </Badge>
                  </button>
                ))}
              </div>
            ))}
          </div>
          {arbol?.advertencia && (
            <p className="text-xs text-alerta flex gap-1">
              <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />
              {arbol.advertencia}
            </p>
          )}
        </div>

        {/* Resultados */}
        <div className="space-y-4">
          {cargando ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              {pareceDescripcionProducto(q) && (
                <div className="space-y-1">
                  <p className="text-sm font-medium">Interpretando tu descripción…</p>
                  <p className="text-xs text-muted-foreground max-w-sm">
                    Tu búsqueda parece la descripción de un producto, así que la estamos traduciendo
                    al vocabulario del arancel. Suele tardar unos segundos.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Cómo entendió la IA la consulta: se muestra COMPLETO para que
                  el usuario audite la traducción (qué buscó y qué ignoró) en
                  vez de confiar a ciegas. Los candidatos salen de la base. */}
              {respuesta?.interpretacion && (
                <div className="rounded-md border border-l-4 border-l-primary p-4 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5" /> Cómo entendimos tu búsqueda
                      {!bandaAbierta && (
                        <span className="normal-case tracking-normal font-normal text-foreground">
                          — {respuesta.interpretacion.concepto}
                        </span>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={alternarBanda}
                      className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                    >
                      {bandaAbierta ? 'Plegar' : 'Ver el detalle'}
                    </button>
                  </div>
                  {bandaAbierta && (
                    <>
                      <div className="grid gap-1.5 text-sm">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="w-16 shrink-0 text-xs text-muted-foreground">
                            Entendí
                          </span>
                          <span className="font-medium">{respuesta.interpretacion.concepto}</span>
                        </div>
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="w-16 shrink-0 text-xs text-muted-foreground">
                            Busqué
                          </span>
                          <TerminosBuscados
                            terminosLegales={respuesta.interpretacion.terminosLegales}
                            conteos={respuesta.conteos}
                          />
                        </div>
                        <LineaTraduje traducciones={respuesta.interpretacion.traducciones} />
                        <LineaIgnore
                          descartado={respuesta.interpretacion.descartado}
                          traducciones={respuesta.interpretacion.traducciones}
                        />
                        {/* Cómo se desempató: son frases del libro, no de la IA.
                            Si aparecen, el orden de los resultados se decidió
                            con ellas (segunda pasada, tarea B4). */}
                        {(respuesta.ramasDelCapitulo ?? []).length > 0 && (
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <span className="w-16 shrink-0 text-xs text-muted-foreground">
                              Desempaté
                            </span>
                            <span className="flex flex-wrap items-baseline gap-1">
                              <GitBranch className="h-3 w-3 shrink-0 translate-y-0.5 text-muted-foreground" />
                              {respuesta.ramasDelCapitulo!.map((rama) => (
                                <Badge key={rama} variant="outline" className="font-normal">
                                  {rama}
                                </Badge>
                              ))}
                              <span className="text-xs text-muted-foreground">
                                — con estas frases del arancel, no con criterio propio
                              </span>
                            </span>
                          </div>
                        )}
                      </div>
                      {/* Una respuesta apoyada en internet se veía igual que una
                          que salió del arancel. El usuario tiene que poder
                          pesarlas distinto (vault 126 §5.4). */}
                      {respuesta.usoInternet && (
                        <p className="flex items-start gap-1.5 rounded-md bg-muted/60 px-2.5 py-2 text-xs text-muted-foreground">
                          <Globe className="h-3.5 w-3.5 shrink-0 translate-y-px" />
                          <span>
                            No reconocí ese producto, así que{' '}
                            <span className="font-medium text-foreground">
                              busqué en internet qué es
                            </span>
                            . La partida y lo que paga siguen saliendo solo del arancel oficial.
                          </span>
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground border-t pt-2">
                        La inteligencia artificial solo traduce tu vocabulario al del arancel. Las
                        partidas y sus alícuotas salen siempre del arancel oficial vigente — nunca
                        las inventa. Si la traducción no es lo que buscás, probá reescribiendo la
                        búsqueda.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Capítulos sugeridos por nota legal: el término vive en una
                  nota, no en las descripciones ("ropa" → cap. 63). Resultado a
                  nivel capítulo CON la cita, nunca 60 partidas idénticas. */}
              {(respuesta?.capitulosSugeridos ?? []).map((c) => (
                <div
                  key={`${c.chapter}-${c.noteType}`}
                  className="rounded-md border border-primary/40 bg-primary/5 p-4 space-y-2"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold flex items-center gap-2">
                      <ScrollText className="h-4 w-4 shrink-0 text-primary" />
                      Capítulo {c.chapter}
                      {c.chapterName ? ` — ${c.chapterName}` : ''}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCapitulo(c.chapter ?? undefined)}
                    >
                      Ver las {c.totalPartidasDelCapitulo} partidas del capítulo
                    </Button>
                  </div>
                  <blockquote className="rounded bg-muted/60 px-3 py-2 text-sm italic">
                    “<CitaResaltada fragmento={c.fragmento} />”
                  </blockquote>
                  <p className="text-xs text-muted-foreground">
                    Una nota legal de este capítulo menciona lo que buscaste. Las notas son parte
                    del texto legal del arancel y mandan por encima de la descripción de cada
                    partida.
                  </p>
                </div>
              ))}

              {/* El intérprete se intentó y no estaba disponible. Va ANTES del
                  aviso de "aproximados" porque es la causa, no el efecto: sin
                  esto la pantalla culpaba a la consulta ("no encontramos
                  coincidencia exacta") cuando el problema era del servicio.
                  Caso real: 2026-08-10, key vencida en la VM. */}
              {respuesta?.interpreteNoDisponible && (
                <div className="flex gap-2 rounded-md border border-alerta-borde bg-alerta-suave p-3 text-sm ">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-alerta " />
                  <span>
                    <strong>La traducción automática no está funcionando</strong> — buscamos tus
                    palabras tal como las escribiste, sin traducirlas al vocabulario del arancel.
                    Puede que falten partidas que el arancel nombra de otra forma. Las alícuotas que
                    ves son las oficiales y son correctas. Si esto sigue así, avisale a Datec.
                  </span>
                </div>
              )}

              {/* Aproximados: el AND exacto dio 0 y se cayó a la red de
                  seguridad OR — se avisa SIEMPRE, nunca se disfraza de match. */}
              {respuesta?.matchParcial && resultados.length > 0 && (
                <div className="rounded-md border border-alerta-borde bg-alerta-suave p-3 text-sm flex gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-alerta " />
                  <span>
                    <strong>Resultados aproximados</strong> — no encontramos una coincidencia exacta
                    con todas tus palabras. Están ordenados por cuántas coinciden: revisá la
                    descripción antes de elegir.
                  </span>
                </div>
              )}

              {/* 2–5 candidatos EMPATADOS en el consenso: la decisión final
                  es HUMANA — botones, no una segunda llamada de IA. El resto
                  de los resultados sigue disponible en la tabla de abajo. */}
              {nClarif > 0 && (
                <div className="rounded-md border p-4 space-y-3">
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <HelpCircle className="h-4 w-4 text-primary" /> ¿Cuál describe mejor tu
                      producto?
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      La búsqueda quedó empatada entre {nClarif} partidas. Elegí una para ver su
                      ficha completa con tributos y notas legales.
                    </p>
                  </div>
                  <div className="grid gap-2">
                    {resultados.slice(0, nClarif).map((r) => {
                      // La cadena de padres sin la hoja final (que ya es la
                      // descripción del botón). Sin esto, dos candidatos que
                      // dicen "Las demás" son indistinguibles — y elegir mal
                      // acá es elegir mal la partida.
                      const contexto = (r.fullContext ?? '')
                        .split(' > ')
                        .map((s) => s.trim().replace(/:$/, ''))
                        .filter(Boolean)
                        .slice(0, -1)
                        .join(' › ');
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => abrirDetalle(r.code)}
                          className="flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors hover:border-primary hover:bg-primary/5"
                        >
                          <span className="font-mono text-xs font-bold shrink-0">{r.code}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm">{r.description}</span>
                            {contexto && (
                              <span
                                className="block text-xs text-muted-foreground mt-0.5 line-clamp-2"
                                title={contexto}
                              >
                                {contexto}
                              </span>
                            )}
                          </span>
                          <span className="flex shrink-0 items-center gap-1.5">
                            {r.importProhibited === true && (
                              <Badge variant="destructive">No importable</Badge>
                            )}
                            {r.hasIce === true && <Badge variant="destructive">ICE</Badge>}
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              GA {pct(r.gaRate)}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {capituloVista ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setCapituloVista(null)}>
                      ← Volver a los resultados
                    </Button>
                    <Badge variant="secondary">Capítulo {capituloVista}</Badge>
                    <span className="text-xs text-muted-foreground">
                      Las partidas que aparecieron en tu búsqueda están marcadas.
                    </span>
                  </div>
                  <ArbolArancel
                    capitulo={capituloVista}
                    onElegirPartida={abrirDetalle}
                    resaltar={
                      new Set(
                        resultados.filter((r) => r.chapter === capituloVista).map((r) => r.code)
                      )
                    }
                  />
                </div>
              ) : capituloNavegado ? (
                <ArbolArancel capitulo={capituloNavegado} onElegirPartida={abrirDetalle} />
              ) : resultados.length === 0 ? (
                (respuesta?.capitulosSugeridos ?? []).length > 0 ? (
                  <div className="rounded-md border p-4 space-y-1.5">
                    <p className="text-sm font-medium">
                      No encontramos partidas que digan “{q.trim()}” en su descripción.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      El arancel suele usar otras palabras. La sugerencia de arriba sale de una nota
                      legal que sí menciona tu término: entrá al capítulo y buscá tu partida ahí.
                      Cuando la encuentres, guardá tu palabra como sinónimo para la próxima.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed p-8 text-center space-y-3">
                    <p className="font-medium">No encontramos nada con eso.</p>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      Buena parte del arancel tiene descripciones como “Los demás”, que no se pueden
                      buscar por texto. Tenés dos caminos.
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center pt-1">
                      <Badge variant="outline">
                        1. Navegá por capítulo en el panel de la izquierda
                      </Badge>
                      <Badge variant="outline">
                        2. Cuando la encuentres, guardala como sinónimo con tu palabra
                      </Badge>
                    </div>
                  </div>
                )
              ) : resultadosTabla.length === 0 ? null : (
                <div className="space-y-1.5">
                  {nClarif > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Los demás resultados, por si ninguno de los candidatos de arriba encaja:
                    </p>
                  )}
                  {/* Los resultados dentro de su rama del arancel (tarea C1).
                    La tabla plana sigue disponible para los caminos que no
                    pasan por la IA: navegar un capítulo entero o pedir un
                    código son listas, no respuestas a una pregunta, y ahí
                    agrupar por rama no agrega nada. */}
                  {enArbol ? (
                    <div className="max-h-[560px] overflow-auto">
                      <ResultadosArbol
                        resultados={resultadosTabla}
                        terminos={respuesta?.interpretacion?.terminosLegales ?? []}
                        traducciones={respuesta?.interpretacion?.traducciones}
                        idMejor={resultados[0]?.id}
                        onAbrir={abrirDetalle}
                        onVerCapitulo={setCapituloVista}
                      />
                    </div>
                  ) : (
                    /* Scroll propio (feedback de Sergio, vault 101 §1): una búsqueda
                    con 50 resultados no puede hacer scrollear la página entera.
                    `[&>div]:overflow-visible` anula el wrapper con overflow del
                    componente Table — si quedara, sería él el "scroll container"
                    de los th sticky y el encabezado se iría con el scroll. */
                    <div className="max-h-[480px] overflow-auto rounded-md border [&>div]:overflow-visible">
                      <Table>
                        <TableHeader className="[&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-background [&_th]:shadow-[inset_0_-1px_0_hsl(var(--border))]">
                          <TableRow>
                            <TableHead className="w-[140px]">Código</TableHead>
                            <TableHead>Descripción</TableHead>
                            <TableHead className="w-[70px] text-right">GA</TableHead>
                            <TableHead className="w-[70px] text-right">IVA</TableHead>
                            <TableHead className="w-[150px]">Marcas</TableHead>
                            <TableHead className="w-[160px]">Origen</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {resultadosTabla.map((r, i) => (
                            <TableRow
                              key={`${r.id}-${r.origen}-${i}`}
                              className="cursor-pointer"
                              onClick={() => abrirDetalle(r.code)}
                            >
                              <TableCell className="font-mono text-xs font-semibold">
                                {r.code}
                              </TableCell>
                              <TableCell className="text-sm">
                                {r.origen === 'APERTURA' ? (
                                  <>
                                    <span className="font-medium">{r.descripcionInterna}</span>
                                    <span className="text-muted-foreground text-xs block">
                                      cuelga de {r.codigoOficialPadre} · {r.description}
                                    </span>
                                  </>
                                ) : (
                                  r.description
                                )}
                              </TableCell>
                              <TableCell className="text-right text-sm">{pct(r.gaRate)}</TableCell>
                              <TableCell className="text-right text-sm">{pct(r.ivaRate)}</TableCell>
                              <TableCell className="space-x-1">
                                {/* Primero de todos: quien busca una partida tiene que
                                ver que no puede importarse ANTES de elegirla, no
                                después de haber cotizado con ella. */}
                                {r.importProhibited === true && (
                                  <Badge
                                    variant="destructive"
                                    title="Esta mercadería no puede importarse a Bolivia"
                                  >
                                    No importable
                                  </Badge>
                                )}
                                {r.isIvaExempt && <Badge variant="secondary">IVA exento</Badge>}
                                {r.hasIce === true && <Badge variant="destructive">ICE</Badge>}
                                {r.hasIce === null && (
                                  <Badge variant="outline" title="No tenemos el dato de ICE">
                                    ICE sin dato
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {ORIGEN_LABELS[r.origen]}
                                {r.viaTermino ? `: “${r.viaTermino}”` : ''}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {/* Debajo de los resultados y solo si hubo consulta: navegar un
              capítulo no es una pregunta y no hay nada que calificar. */}
          {!cargando && !capituloNavegado && respuesta?.busquedaId && (
            <CalificarBusqueda busquedaId={respuesta.busquedaId} />
          )}
          {!cargando && respuesta?.version && (
            <p className="text-xs text-muted-foreground">
              Arancel vigente: {respuesta.version.nombre}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Vista 2 — Mis aperturas
// ============================================================
export function VistaAperturas() {
  const { toast } = useToast();
  const [items, setItems] = useState<TenantSubheading[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [importAbierto, setImportAbierto] = useState(false);
  const [editando, setEditando] = useState<TenantSubheading | null>(null);
  const [padre, setPadre] = useState<ResultadoBusqueda | null>(null);
  const [codigoInterno, setCodigoInterno] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setItems(await tariffCatalogService.aperturas());
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setPadre(null);
    setCodigoInterno('');
    setDescripcion('');
    setNotas('');
    setAbierto(true);
  };

  const guardar = async () => {
    if (!editando && !padre) {
      toast({ title: 'Elegí la partida oficial de la que cuelga', variant: 'destructive' });
      return;
    }
    setGuardando(true);
    try {
      if (editando) {
        await tariffCatalogService.actualizarApertura(editando.id, {
          internalCode: codigoInterno,
          description: descripcion,
          notes: notas || null,
          ...(padre ? { tariffHeadingId: padre.id } : {}),
        });
        toast({ title: 'Apertura actualizada', variant: 'success' });
      } else {
        await tariffCatalogService.crearApertura({
          tariffHeadingId: padre!.id,
          internalCode: codigoInterno,
          description: descripcion,
          notes: notas || null,
        });
        toast({ title: 'Apertura creada', variant: 'success' });
      }
      setAbierto(false);
      cargar();
    } catch (e: unknown) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id: string) => {
    try {
      await tariffCatalogService.eliminarApertura(id);
      toast({ title: 'Apertura eliminada', variant: 'success' });
      cargar();
    } catch (e: unknown) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Una apertura es tu propia subdivisión de una partida oficial, con el código que usás
          internamente. <strong>Las alícuotas las hereda del arancel</strong> y no se pueden editar:
          a la aduana viaja siempre el código oficial.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setImportAbierto(true)}>
            <Upload className="h-4 w-4 mr-2" /> Importar CSV
          </Button>
          <Button onClick={abrirNuevo}>
            <Plus className="h-4 w-4 mr-2" /> Nueva apertura
          </Button>
        </div>
      </div>

      {cargando ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          Todavía no cargaste ninguna apertura. Podés crearlas de a una o subir un CSV.
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tu código</TableHead>
                <TableHead>Tu descripción</TableHead>
                <TableHead>Cuelga del código oficial</TableHead>
                <TableHead className="text-right">GA</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="w-[90px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono text-xs font-semibold">
                    {a.internalCode}
                  </TableCell>
                  <TableCell className="text-sm">{a.description}</TableCell>
                  <TableCell className="text-sm">
                    <span className="font-mono text-xs">{a.tariffHeading?.code}</span>
                    <span className="text-muted-foreground text-xs block truncate max-w-[280px]">
                      {a.tariffHeading?.description}
                    </span>
                  </TableCell>
                  {/* Heredadas: se muestran en gris para que se lea que no son editables */}
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {a.tariffHeading ? pct(a.tariffHeading.gaRate) : '—'}
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {a.tariffHeading
                      ? a.tariffHeading.isIvaExempt
                        ? 'Exento'
                        : pct(a.tariffHeading.ivaRate)
                      : '—'}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditando(a);
                        setPadre(null);
                        setCodigoInterno(a.internalCode);
                        setDescripcion(a.description);
                        setNotas(a.notes ?? '');
                        setAbierto(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => eliminar(a.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar apertura' : 'Nueva apertura interna'}</DialogTitle>
            <DialogDescription>
              Las alícuotas salen del arancel oficial. Acá solo definís tu código y tu descripción.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Partida oficial de la que cuelga</Label>
              {editando && !padre && (
                <p className="text-xs text-muted-foreground">
                  Actual: <span className="font-mono">{editando.tariffHeading?.code}</span>. Buscá
                  otra solo si querés cambiarla.
                </p>
              )}
              <BuscadorPartida onSeleccionar={setPadre} seleccionada={padre} />
            </div>
            <div className="space-y-2">
              <Label>Tu código interno</Label>
              <Input
                value={codigoInterno}
                onChange={(e) => setCodigoInterno(e.target.value)}
                placeholder="LAP-DELL-5420"
              />
            </div>
            <div className="space-y-2">
              <Label>Tu descripción</Label>
              <Input
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Laptop Dell Latitude 5420"
              />
            </div>
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea value={notas} onChange={(e) => setNotas(e.target.value)} rows={2} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>
                Cancelar
              </Button>
              <Button onClick={guardar} disabled={guardando || !codigoInterno || !descripcion}>
                {guardando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MasterDataImportDialog
        open={importAbierto}
        onOpenChange={setImportAbierto}
        entityName="Aperturas arancelarias"
        templateHeaders={['codigo_oficial', 'codigo_interno', 'descripcion', 'notas']}
        templateRows={[
          [
            '8471.30.00.90',
            'LAP-DELL-5420',
            'Laptop Dell Latitude 5420',
            'para Importadora La Paz',
          ],
          ['5208.11.00.00', 'TEL-CRUDO-01', 'Tela de algodón cruda', ''],
        ]}
        helpText="codigo_oficial tiene que existir en el arancel vigente. Si el codigo_interno ya existe, se actualiza en vez de duplicar."
        importFn={(file) => tariffCatalogService.importarAperturas(file)}
        onSuccess={cargar}
      />
    </div>
  );
}

// ============================================================
// Vista 3 — Mis sinónimos
// ============================================================
export function VistaSinonimos() {
  const { toast } = useToast();
  const [items, setItems] = useState<TariffSynonym[]>([]);
  const [cargando, setCargando] = useState(true);
  const [termino, setTermino] = useState('');
  const [padre, setPadre] = useState<ResultadoBusqueda | null>(null);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setItems(await tariffCatalogService.sinonimos());
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const agregar = async () => {
    if (!padre || termino.trim().length < 2) return;
    setGuardando(true);
    try {
      await tariffCatalogService.crearSinonimo({ tariffHeadingId: padre.id, term: termino.trim() });
      toast({ title: 'Sinónimo agregado', variant: 'success' });
      setTermino('');
      setPadre(null);
      cargar();
    } catch (e: unknown) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground max-w-3xl">
        Un sinónimo es tu palabra apuntando a una partida del arancel. Sirve cuando el arancel usa
        otro vocabulario que el tuyo: por ejemplo el arancel dice <em>“máquinas”</em>, así que
        buscar <em>“maquinaria”</em> no devuelve nada hasta que lo registres acá.
      </p>

      <div className="rounded-md border p-4 space-y-3">
        <p className="text-sm font-semibold">Agregar sinónimo</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Tu palabra</Label>
            <Input
              value={termino}
              onChange={(e) => setTermino(e.target.value)}
              placeholder="maquinaria"
            />
          </div>
          <div className="space-y-2">
            <Label>Apunta a la partida</Label>
            <BuscadorPartida onSeleccionar={setPadre} seleccionada={padre} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={agregar} disabled={guardando || !padre || termino.trim().length < 2}>
            {guardando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Plus className="h-4 w-4 mr-2" /> Agregar
          </Button>
        </div>
      </div>

      {cargando ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          Todavía no hay sinónimos. Son la forma más rápida de que el buscador hable tu idioma.
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tu palabra</TableHead>
                <TableHead>Apunta a</TableHead>
                <TableHead className="w-[60px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.term}</TableCell>
                  <TableCell className="text-sm">
                    <span className="font-mono text-xs mr-2">{s.tariffHeading?.code}</span>
                    {s.tariffHeading?.description}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        await tariffCatalogService.eliminarSinonimo(s.id);
                        cargar();
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Vista 4 — Versiones del arancel (import y activación: PLATFORM_ADMIN)
// ============================================================
export function VistaVersiones() {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const esPlatformAdmin = user?.role === 'PLATFORM_ADMIN';

  const [items, setItems] = useState<TariffVersion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [nombre, setNombre] = useState('');
  const [norma, setNorma] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [resultado, setResultado] = useState<ResultadoImport | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setItems(await tariffCatalogService.versiones());
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const descargarPlantilla = () => {
    const filas = [
      [
        'codigo',
        'descripcion',
        'ga_porcentaje',
        'iva_porcentaje',
        'iva_exento',
        'seccion',
        'capitulo',
        'nombre_capitulo',
        'unidad',
      ],
      // Filas de EJEMPLO: solo muestran el formato. Las alícuotas reales salen
      // del arancel publicado, no de acá — de ahí el texto explícito.
      [
        '5208.11.00.00',
        'EJEMPLO - reemplazar por la descripción oficial',
        '0',
        '13',
        'no',
        'XI',
        '52',
        'Algodon',
        'm2',
      ],
      [
        '3004.90.30.00',
        'EJEMPLO - reemplazar por la descripción oficial',
        '0',
        '13',
        'si',
        'VI',
        '30',
        'Productos farmaceuticos',
        'kg',
      ],
    ];
    const csv = filas.map((f) => f.map((c) => `"${c}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_arancel.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const importar = async () => {
    if (!archivo || !nombre || !desde) return;
    setSubiendo(true);
    setResultado(null);
    try {
      const r = await tariffCatalogService.importarArancel(archivo, {
        nombre,
        norma: norma || undefined,
        vigenciaDesde: desde,
        vigenciaHasta: hasta || undefined,
      });
      setResultado(r);
      toast({ title: `Se cargaron ${r.created} partidas`, variant: 'success' });
      cargar();
    } catch (e: unknown) {
      toast({
        title: 'No se pudo importar',
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setSubiendo(false);
    }
  };

  const alternar = async (v: TariffVersion) => {
    try {
      await tariffCatalogService.activarVersion(v.id, !v.isActive);
      toast({ title: v.isActive ? 'Versión desactivada' : 'Versión activada', variant: 'success' });
      cargar();
    } catch (e: unknown) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : undefined,
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          El arancel es ley nacional: lo mantiene Datec y es el mismo para todos los clientes. Por
          eso nadie puede editar una partida desde acá — solo se carga una versión nueva y se activa
          cuando fue revisada.
        </p>
        {esPlatformAdmin && (
          <Button onClick={() => setAbierto(true)}>
            <Upload className="h-4 w-4 mr-2" /> Importar arancel
          </Button>
        )}
      </div>

      {cargando ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Versión</TableHead>
                <TableHead>Norma</TableHead>
                <TableHead>Vigencia</TableHead>
                <TableHead className="text-right">Partidas</TableHead>
                <TableHead>Estado</TableHead>
                {esPlatformAdmin && <TableHead className="w-[120px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="text-sm font-medium">{v.name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {v.legalBasis ?? '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {v.validFrom.slice(0, 10)} → {v.validTo ? v.validTo.slice(0, 10) : 'sin fin'}
                  </TableCell>
                  <TableCell className="text-right text-sm">{v._count?.headings ?? '—'}</TableCell>
                  <TableCell>
                    {v.isActive ? (
                      <Badge variant="success" className="gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Activa
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1">
                        <XCircle className="h-3 w-3" /> Inactiva
                      </Badge>
                    )}
                  </TableCell>
                  {esPlatformAdmin && (
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => alternar(v)}>
                        {v.isActive ? 'Desactivar' : 'Activar'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Importar un arancel oficial</DialogTitle>
            <DialogDescription>
              Se crea una versión nueva e <strong>inactiva</strong>. Revisá los conteos por capítulo
              y activala recién después.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Nombre de la versión</Label>
                <Input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Arancel 2028 — DS-XXXX"
                />
              </div>
              <div className="space-y-2">
                <Label>Norma de respaldo (opcional)</Label>
                <Input
                  value={norma}
                  onChange={(e) => setNorma(e.target.value)}
                  placeholder="RM N°..."
                />
              </div>
              <div className="space-y-2">
                <Label>Vigente desde</Label>
                <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Vigente hasta (opcional)</Label>
                <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Archivo CSV</Label>
                <Button variant="ghost" size="sm" onClick={descargarPlantilla}>
                  Descargar plantilla
                </Button>
              </div>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
              />
              <p className="text-xs text-muted-foreground">
                Columnas: codigo, descripcion, ga_porcentaje, iva_porcentaje, iva_exento, seccion,
                capitulo, nombre_capitulo, unidad. Los porcentajes van como número (15 = 15%).
              </p>
            </div>

            {resultado && (
              <div className="rounded-md border p-3 space-y-2 text-sm max-h-64 overflow-y-auto">
                <p className="font-semibold">Se cargaron {resultado.created} partidas.</p>
                {(resultado.advertencias ?? []).map((a, i) => (
                  <p key={i} className="text-alerta flex gap-1">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    {a}
                  </p>
                ))}
                {resultado.capitulos && resultado.capitulos.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {resultado.capitulos.map((c) => (
                      <Badge key={c.chapter} variant={c.partidas < 5 ? 'destructive' : 'secondary'}>
                        {c.chapter}: {c.partidas}
                      </Badge>
                    ))}
                  </div>
                )}
                {resultado.errors.length > 0 && (
                  <div className="pt-1">
                    <p className="font-medium text-destructive">
                      {resultado.errors.length} fila(s) con error:
                    </p>
                    <ul className="text-xs space-y-0.5 mt-1">
                      {resultado.errors.slice(0, 20).map((er, i) => (
                        <li key={i}>
                          Fila {er.row}: {er.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>
                Cerrar
              </Button>
              <Button onClick={importar} disabled={subiendo || !archivo || !nombre || !desde}>
                {subiendo && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Importar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// El contenedor con el switcher de vistas vive en la página
// (tariff-assistant-page.tsx): necesita mantener VistaExplorar montada
// mientras la ficha está abierta para no perder la búsqueda al volver.
